import { Button, DrawerClose, DrawerFooter } from "@monbits/ui";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import userProvider, {
	type MerchantDetails,
	type UpsertUserWithdrawalFeeRequest,
	type UserDetails,
	type UserSettings,
} from "src/providers/user-provider";
import auditQueries from "src/queries/audit-queries";
import settingsQueries from "src/queries/settings-queries";
import withdrawalFeeQueries from "src/queries/withdrawal-fee-queries";
import { EntityDrawer } from "@/components/entity-drawer";
import { UserDrawerContent } from "@/components/user-drawer-content";
export function UserDetail() {
	const { userId } = useParams<{ userId: string }>();
	const [user, setUser] = React.useState<UserDetails | null>(null);
	const [isLoading, setIsLoading] = React.useState(true);
	const [isNotFound, setIsNotFound] = React.useState(false);
	const [_userSettings, setUserSettings] = React.useState<UserSettings | null>(
		null,
	);
	const [isLoadingSettings, setIsLoadingSettings] = React.useState(false);
	const [isSavingSettings, setIsSavingSettings] = React.useState(false);
	const [serviceFeeRate, setServiceFeeRate] = React.useState<string>("");
	const [maxMerchants, setMaxMerchants] = React.useState<string>("");
	const [blockReason, setBlockReason] = React.useState("");
	const [isBlocking, setIsBlocking] = React.useState(false);
	const [userMerchants, setUserMerchants] = React.useState<MerchantDetails[]>(
		[],
	);
	const [isLoadingMerchants, setIsLoadingMerchants] = React.useState(false);

	const {
		data: auditData,
		isLoading: isLoadingAudit,
		refetch: refetchAudit,
	} = useQuery(auditQueries.getUserAuditLogs(userId ?? "", 20, 0));

	const { data: withdrawalFees, isLoading: isLoadingWithdrawalFees } =
		withdrawalFeeQueries.listUserWithdrawalFees(userId ?? "");

	const { data: currencySettings } = settingsQueries.listCurrencySettings(true);

	const upsertMutation = withdrawalFeeQueries.upsertUserWithdrawalFee();
	const deleteMutation = withdrawalFeeQueries.deleteUserWithdrawalFee();

	React.useEffect(() => {
		if (!userId) {
			setIsNotFound(true);
			setIsLoading(false);
			return;
		}

		const loadUser = async () => {
			try {
				setIsLoading(true);
				setIsNotFound(false);

				const userData = await userProvider.getUser(userId);
				setUser(userData);
				setBlockReason("");

				setIsLoadingSettings(true);
				try {
					const settings = await userProvider.getUserSettings(userId);
					setUserSettings(settings);
					setServiceFeeRate(settings.serviceFeeRate?.toString() ?? "");
					setMaxMerchants(settings.maxMerchants?.toString() ?? "");
				} catch (error) {
					console.error("Failed to load user settings:", error);
				} finally {
					setIsLoadingSettings(false);
				}

				setIsLoadingMerchants(true);
				try {
					const merchants = await userProvider.getUserMerchants(userId);
					setUserMerchants(merchants);
				} catch (error) {
					console.error("Failed to load user merchants:", error);
				} finally {
					setIsLoadingMerchants(false);
				}
			} catch (error) {
				console.error("Failed to load user:", error);
				setIsNotFound(true);
			} finally {
				setIsLoading(false);
			}
		};

		loadUser();
	}, [userId]);

	const handleSaveSettings = React.useCallback(async () => {
		if (!user) return;

		try {
			setIsSavingSettings(true);
			const settings: UserSettings = {};

			if (serviceFeeRate.trim()) {
				const rate = Number.parseFloat(serviceFeeRate);
				if (!Number.isNaN(rate)) {
					settings.serviceFeeRate = rate;
				}
			}

			if (maxMerchants.trim()) {
				const max = Number.parseInt(maxMerchants, 10);
				if (!Number.isNaN(max)) {
					settings.maxMerchants = max;
				}
			}

			await userProvider.updateUserSettings(user.uuid, settings);
			toast.success("User settings updated successfully");
			setUserSettings(settings);
			refetchAudit();
		} catch (error) {
			console.error("Failed to update user settings:", error);
			toast.error("Failed to update user settings");
		} finally {
			setIsSavingSettings(false);
		}
	}, [user, serviceFeeRate, maxMerchants, refetchAudit]);

	const handleSaveWithdrawalFee = React.useCallback(
		async (fee: UpsertUserWithdrawalFeeRequest) => {
			if (!user) return;
			try {
				await upsertMutation.mutateAsync({ userId: user.uuid, fee });
				toast.success("Withdrawal fee override saved");
				refetchAudit();
			} catch (error) {
				console.error("Failed to update withdrawal fee:", error);
				toast.error("Could not save withdrawal fee. Try again.");
				throw error;
			}
		},
		[user, upsertMutation, refetchAudit],
	);

	const handleDeleteWithdrawalFee = React.useCallback(
		async (ticker: string, feeProfile?: string | null) => {
			if (!user) return;
			try {
				await deleteMutation.mutateAsync({
					userId: user.uuid,
					ticker,
					feeProfile,
				});
				toast.success("Withdrawal fee override removed");
				refetchAudit();
			} catch (error) {
				console.error("Failed to delete withdrawal fee:", error);
				toast.error("Could not remove withdrawal fee. Try again.");
			}
		},
		[user, deleteMutation, refetchAudit],
	);

	const handleBlockUser = React.useCallback(async () => {
		if (!user || !blockReason.trim()) return;

		try {
			setIsBlocking(true);
			await userProvider.blockUser(user.uuid, blockReason.trim());
			toast.success(`User ${user.email} has been blocked`);
			refetchAudit();

			const updatedUser = {
				...user,
				blockedAt: new Date().toISOString(),
				blockedReason: blockReason.trim(),
			};
			setUser(updatedUser);
		} catch (error) {
			console.error("Failed to block user:", error);
			toast.error("Failed to block user");
		} finally {
			setIsBlocking(false);
		}
	}, [user, blockReason, refetchAudit]);

	const handleUnblockUser = React.useCallback(async () => {
		if (!user) return;

		try {
			setIsBlocking(true);
			await userProvider.unblockUser(user.uuid);
			toast.success(`User ${user.email} has been unblocked`);
			refetchAudit();

			const updatedUser = {
				...user,
				blockedAt: undefined,
				blockedReason: undefined,
			};
			setUser(updatedUser);
		} catch (error) {
			console.error("Failed to unblock user:", error);
			toast.error("Failed to unblock user");
		} finally {
			setIsBlocking(false);
		}
	}, [user, refetchAudit]);

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<EntityDrawer
			title={user?.blockedAt ? "Blocked User" : "User Management"}
			isLoading={isLoading}
			isNotFound={isNotFound}
			notFoundMessage="User not found"
			parentPath="/users"
		>
			{user && (
				<>
					<div className="flex-1 overflow-y-auto">
						<UserDrawerContent
							user={user}
							userMerchants={userMerchants}
							isLoadingMerchants={isLoadingMerchants}
							isLoadingSettings={isLoadingSettings}
							serviceFeeRate={serviceFeeRate}
							maxMerchants={maxMerchants}
							blockReason={blockReason}
							isBlocking={isBlocking}
							auditLogs={auditData?.logs ?? []}
							isLoadingAudit={isLoadingAudit}
							onServiceFeeRateChange={setServiceFeeRate}
							onMaxMerchantsChange={setMaxMerchants}
							onBlockReasonChange={setBlockReason}
							onBlockUser={handleBlockUser}
							onUnblockUser={handleUnblockUser}
							formatDate={formatDate}
							withdrawalFees={withdrawalFees ?? []}
							isLoadingWithdrawalFees={isLoadingWithdrawalFees}
							isSavingWithdrawalFee={upsertMutation.isPending}
							availableCurrencies={currencySettings ?? []}
							onSaveWithdrawalFee={handleSaveWithdrawalFee}
							onDeleteWithdrawalFee={handleDeleteWithdrawalFee}
						/>
					</div>

					<DrawerFooter className="flex-shrink-0">
						<div className="flex gap-3">
							<Button
								onClick={handleSaveSettings}
								disabled={isSavingSettings || isLoadingSettings}
								className="flex-1"
								size="lg"
							>
								{isSavingSettings ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Saving...
									</>
								) : (
									"Save Settings"
								)}
							</Button>
							<DrawerClose asChild>
								<Button
									variant="outline"
									className="flex-1"
									size="lg"
									disabled={isSavingSettings || isBlocking}
								>
									Close
								</Button>
							</DrawerClose>
						</div>
					</DrawerFooter>
				</>
			)}
		</EntityDrawer>
	);
}
